import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { spaceName } from "@/lib/format";
import { VisibilityPicker } from "./VisibilityIcon";
import { NameChips } from "./NameChips";
import { waitForJob } from "./MoveDialog";

const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

// Admin: turn search results (all, or just the selected ones) into a new album
export const FromSearchDialog = ({ open, onClose, q, space, total, selected }) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [target, setTarget] = useState("private");
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(titleCase(q)); setTarget("private"); setScope(selected.length ? "selected" : "all"); setBusy(false); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault();
    const body = { name, target, q, space, item_ids: scope === "selected" ? selected : null };
    setBusy(true);
    try {
      const { data: p } = await api.post("/albums/from-search/preview", body);
      let move = false;
      if (p.other) {
        move = window.confirm(`${p.other} of ${p.total} photos are saved somewhere else.\n\nMove ${p.other} photo${p.other === 1 ? "" : "s"} to ${spaceName(target)} too? (Cancel leaves them out of the album.)`);
      }
      const { data } = await api.post("/albums/from-search", { ...body, move });
      let r = data;
      if (data.job_id) {
        const id = toast.loading(`Filing 0 of ${data.total}…`);
        r = await waitForJob(data.job_id, (j) => toast.loading(`Filing ${j.pos} of ${j.total}…`, { id }));
        toast.dismiss(id);
      }
      toast.success(`Album created with ${r.done} photo${r.done === 1 ? "" : "s"}${r.left_out ? ` · ${r.left_out} left out` : ""}${r.failed ? ` · ${r.failed} failed` : ""}`);
      onClose();
      navigate(`/albums/${data.album.id}`);
    } catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md bg-[#f4f3f0]" data-testid="from-search-dialog">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Make album from these</DialogTitle>
          <DialogDescription>Who can see it?</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <input className="login-input !mb-0" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Album name" data-testid="from-search-name" />
          {open && <NameChips q={q} space={space} itemIds={scope === "selected" ? selected : null} onPick={setName} testid="from-search-name-chips" />}
          <VisibilityPicker value={target} onChange={setTarget} testid="from-search-visibility" />
          <div className="flex flex-col gap-1.5 text-sm text-neutral-700">
            <label className="flex gap-2 items-center"><input type="radio" checked={scope === "all"} onChange={() => setScope("all")} data-testid="from-search-scope-all" /> Include all {total ?? ""} results</label>
            <label className={`flex gap-2 items-center ${selected.length ? "" : "opacity-50"}`}>
              <input type="radio" disabled={!selected.length} checked={scope === "selected"} onChange={() => setScope("selected")} data-testid="from-search-scope-selected" /> Only selected ({selected.length})
            </label>
          </div>
          <button className="btn-ink" disabled={busy} data-testid="from-search-create">{busy ? "Creating…" : "Create album"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
